import { Form, Formik, FormikProps } from 'formik';
import React, { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  PrimaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { Autocomplete } from 'src/components/shared/Forms/Autocomplete/Autocomplete';
import { FormField } from 'src/components/shared/Forms/FormHelpers';
import { InputField } from 'src/components/shared/Forms/Input/InputField';
import { ModalFooter } from 'src/components/shared/Modal/Modal';
import { GradeDto } from 'src/store/grades/grade';
import { GradeApi } from 'src/store/grades/grades.api';
import { selectActiveGrades } from 'src/store/grades/grades.selectors';
import { ProductDetailDto } from 'src/store/products/product';
import { RunSystemDto } from 'src/store/runsystems/runsystem';
import { selectActiveRunSystems } from 'src/store/runsystems/runsystems.selectors';
import {
  required,
  runValidations,
  validationRunner
} from 'src/utils/validation';

type Props = {
  run: ProductDetailDto;
  onCancel: () => void;
  onSubmit: (product: ProductDetailDto) => void;
};

export const ProductsRunForm = forwardRef<FormikProps<ProductDetailDto>, Props>(
  (props, ref) => {
    const { run, onSubmit, onCancel } = props;
    const { t } = useTranslation();
    const [showErrors, setShowErrors] = useState<boolean>(false);

    const runSystems$ = useSelector(selectActiveRunSystems);
    const grades$ = useSelector(selectActiveGrades);
    const [grades, setGrades] = useState<GradeDto[]>([]);

    useEffect(() => {
      setGrades(grades$);
    }, [grades$]);

    const onRunSystemChange = async (runSystem: Nullable<RunSystemDto>) => {
      if (runSystem) {
        const response = await GradeApi.getByRunSystem(runSystem.id);
        if (response.ok) {
          setGrades(response.data);
        }
      } else {
        setGrades([]);
      }
    };

    const validate = (run: ProductDetailDto) => {
      const validationRunners = [
        validationRunner('runSystemId', t('runSystem.singular'), required),
        validationRunner('gradeId', t('grade.singular'), required)
      ];
      return runValidations(run, validationRunners);
    };

    return (
      <Formik
        initialValues={run}
        validate={validate}
        innerRef={ref}
        onSubmit={(values, { setSubmitting }) => {
          onSubmit(values);
          setSubmitting(false);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          isSubmitting,
          submitCount
        }) => (
          <Form onSubmit={handleSubmit}>
            <FormField>
              <Autocomplete
                className="mb-16"
                options={runSystems$}
                label={t('runSystem.singular')}
                optionField="name"
                value={values.runSystem}
                onChange={runSystem => {
                  setFieldValue('runSystemId', runSystem?.id);
                  setFieldValue('gradeId', null);
                  onRunSystemChange(runSystem);
                }}
                disabled={runSystems$.length === 0 || run.id !== undefined}
                onBlur={handleBlur}
                errorMessage={errors.runSystemId}
                displayError={!!touched.runSystemId || showErrors}
              />
            </FormField>
            <FormField>
              <Autocomplete
                className="mb-16"
                options={grades}
                label={t('grade.singular')}
                optionField="name"
                value={values.grade}
                onChange={grade => {
                  setFieldValue('gradeId', grade?.id);
                }}
                disabled={!values.runSystemId && grades.length === 0}
                onBlur={handleBlur}
                errorMessage={errors.gradeId}
                displayError={!!touched.gradeId || showErrors}
              />
            </FormField>
            <InputField
              name="description"
              label={t('props.description')}
              value={values.description}
              multiline
              rows={4}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={errors.description}
              displayError={touched.description || submitCount > 0}
            />
            <InputField
              name="runText"
              label={t('product.runText')}
              value={values.runText}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={errors.runText}
              displayError={touched.runText || submitCount > 0}
            />
            <InputField
              name="qaStation"
              label={t('product.qaStation')}
              value={values.qaStation}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={errors.qaStation}
              displayError={touched.qaStation || submitCount > 0}
            />

            <ModalFooter>
              <TertiaryButton
                onClick={onCancel}
                label={t('cancel')}
                className="mr-32"
              />
              <PrimaryButton
                type="submit"
                label={t('save.verb')}
                disabled={isSubmitting}
                onClick={() => {
                  setShowErrors(true);
                }}
              />
            </ModalFooter>
          </Form>
        )}
      </Formik>
    );
  }
);
